import { forwardRef } from 'react';
import type { PropsWithoutRef } from 'react';

import type { ForwardRefForwardPropsComponent, OmitStrict, TestIdProp } from '../../types';
import { useTestIds, useUID } from '../../hooks';
import MultiStepForm from '../MultiStepForm';
import type { BaseFormProps } from '../MultiStepForm/MultiStepForm.types';
import { withTestIds } from '../../utils';

import { getFormTestIds } from './Form.test-ids';

export interface FormProps extends OmitStrict<BaseFormProps, 'content'>, TestIdProp {
  /** The Form content. Recommended composing form elements within a layout template such as Grid or Flex as the only child. */
  children: BaseFormProps['content'];
  /** Label for the form. */
  label?: string;
}

const Form: ForwardRefForwardPropsComponent<FormProps> = forwardRef(function Form(
  {
    testId,
    children,
    actions,
    progress,
    heading,
    label,
    description,
    banners,
    ...restProps
  }: PropsWithoutRef<FormProps>,
  ref: FormProps['ref']
) {
  const stepId = useUID();
  const testIds = useTestIds(testId, getFormTestIds);

  return (
    <MultiStepForm
      data-testid={testIds.root}
      autoComplete='_off'
      {...restProps}
      heading={heading}
      steps={[
        {
          id: stepId,
          name: label ?? '',
          actions,
          content: children,
          description,
          banners
        }
      ]}
      currentStepId={stepId}
      progress={progress}
      ref={ref}
    />
  );
});

export default withTestIds(Form, getFormTestIds);
